/**
 * The streak on You says whether it's guarded (docs/mockups/notifications.html, slide 3):
 * "Protected" while the streak saver is on, with when it comes; "Protect your streak" otherwise,
 * a quiet row that opens the notifications sheet with the streak saver first. Nothing where push
 * can't work, or before there's a streak.
 */
import { Wedge } from '../components/icons.tsx';
import { NotifyTrigger, type SheetRequest } from './host.tsx';
import { useNotifyStatus } from './push.ts';
import '../styles/push.css';

/** What the streak card opens: the notifications sheet, the streak saver first. */
const REQ: SheetRequest = { surface: 'streak_card', first: 'streakSaver' };

export function StreakGuard({ streak }: { streak: number }) {
	const { state, push } = useNotifyStatus();
	if (!state || state === 'unsupported' || streak < 1) return null;
	if (state === 'on' && push.prefs.streakSaver) {
		// The same box as the off state (James, 2 Nov); it opens the settings.
		return (
			<NotifyTrigger req={REQ}>
				<button type="button" className="sg box on" data-testid="streak-guard" data-on="">
					<Shield on />
					<span className="sg-words">
						<b>Protected</b>
						<small>Streak saver at {push.prefs.streakSaverAt}</small>
					</span>
					<Wedge direction="right" className="sg-wg" />
				</button>
			</NotifyTrigger>
		);
	}
	// One quiet row (James, 2 Nov: the red "Not protected" pill was too loud).
	return (
		<NotifyTrigger req={REQ}>
			<button type="button" className="sg box off" data-testid="streak-guard">
				<Shield />
				<span className="sg-words">
					<b>Protect your streak</b>
					<small>A reminder at {push.prefs.streakSaverAt} if you haven’t played.</small>
				</span>
				<Wedge direction="right" className="sg-wg" />
			</button>
		</NotifyTrigger>
	);
}

/** A shield: outlined while the streak isn't guarded, filled with a tick once it is. */
function Shield({ on = false }: { on?: boolean }) {
	return (
		<svg viewBox="0 0 24 26" aria-hidden="true" className={on ? 'sg-shield on' : 'sg-shield'}>
			<path d="M12 1.5l9 3.6V12c0 5.6-4 9.8-9 12.3C7 21.8 3 17.6 3 12V5.1z" />
			{on && <path className="sg-shield-tick" d="M7.8 12.6l3 3 5.6-6" />}
		</svg>
	);
}
